import React, { useEffect, useState, useContext, useRef } from 'react';
import axios from 'axios';
import InventoryCard from './InventoryCard';
import './globalInventory.css';
import MyContext from '../../../MyContext';
import { FolderKanban, LayoutGrid, Boxes, PackageCheck, XCircle, X } from 'lucide-react';
import AddInventoryCategoryModal from './AddInventoryCategoryModal';
import AddInventorySubCategoryModal from './AddInventorySubCategoryModal';
import AddBarcodeModal from './AddBarcodeModal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddItemPrefilledModal from './AddIGlobalModal';
const GlobalInventory = ({ userID }) => {
  const { baseUrl, config } = useContext(MyContext);
  const productRef = useRef(null);
  const carouselRef = useRef(null);

  const [masterCategories, setMasterCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolling, setScrolling] = useState(true);
  const [barcodeItem, setBarcodeItem] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddSubCategory, setShowAddSubCategory] = useState(false);
  // --- Search state ---
  const [searchWord, setSearchWord] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]); // normalized to InventoryCard shape

  // helpers to get selected names
  const selectedCategoryName =
    masterCategories.find((c) => c.id === selectedCategoryId)?.category || '';

  const selectedSubCategoryName =
    subCategories.find((s) => s.id === selectedSubCategoryId)?.subCategory || '';
  const effectiveSubCategoryName =
    selectedSubCategoryName || subCategories?.[0]?.subCategory || '';
  const fetchMasterCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await axios.post(`${baseUrl}/api/Inventory/v1.0/getMasterInventoryCategoriesV2`, {
        userid: userID,
        recordPerPage: '500',
        pageIndex: '1',
      }, config);
      const data = res?.data?.response || [];
      setMasterCategories(data);
      if (data[0]) {
        handleCategorySelect(data[0].id, data[0].subCategories);
      }
    } catch (err) {
      console.error('Error fetching master categories:', err);
      setMasterCategories([]);
    }
    setLoadingCategories(false);
  };

  const fetchProductsByCategory = async (categoryId) => {
    setLoadingItems(true);
    setInventoryItems([]);
    try {
      const res = await axios.post(`${baseUrl}/api/Inventory/v1.0/getMasterInventoryItems`, {
        userid: userID,
        categoryId: categoryId.toString(),
      }, config);
      const data = res?.data?.response || [];
      const flatProducts = [];
      data.forEach(cat => {
        cat.subCategories?.forEach(sub => {
          sub.products?.forEach(prod => {
            flatProducts.push({
              ...prod,
              category: cat.categoryName,
              subCategory: sub.subCategoryName,
            });
          });
        });
      });
      setInventoryItems(flatProducts);
      setScrolling(false);
      setTimeout(() => {
        if (productRef.current) {
          productRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    } catch (err) {
      console.error('Error fetching inventory items:', err);
      setInventoryItems([]);
    }
    setLoadingItems(false);
  };

  const handleCategorySelect = (categoryId, subCats) => {
    setSelectedCategoryId(categoryId);
    setSubCategories(subCats || []);
    // auto-pick first subcategory id (if present)
    const firstSubId = subCats?.[0]?.id ?? null;
    setSelectedSubCategoryId(firstSubId);

    // keep your existing fetch (your API already returns data for first subcat case)
    fetchProductsByCategory(categoryId);
  };

  const toggleItemSelection = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const addSelectedToInventory = async () => {
    if (!selectedItems.length) return toast.info('No items selected');

    try {
      const res = await axios.post(`${baseUrl}/api/Inventory/v1.0/addProductToMainInventory`, {
        userid: userID,
        items: selectedItems
      }, config);

      const { responseCode, responseMessage } = res.data || {};

      if (responseCode === '00') {
        toast.success(responseMessage || 'Items added successfully');
        setSelectedItems([]);
      } else {
        toast.error(responseMessage || 'Failed to add items');
      }
    } catch (err) {
      toast.error('Server error. Please try again.');
    }
  };

  useEffect(() => {
    fetchMasterCategories();
  }, []);

  useEffect(() => {
    let interval;
    if (scrolling && carouselRef.current) {
      interval = setInterval(() => {
        if (carouselRef.current.scrollLeft + carouselRef.current.clientWidth < carouselRef.current.scrollWidth) {
          carouselRef.current.scrollLeft += 1;
        } else {
          carouselRef.current.scrollLeft = 0;
        }
      }, 30);
    }
    return () => clearInterval(interval);
  }, [scrolling]);

  const filteredItems = inventoryItems.filter((item) =>
    item.productName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Server-side search
  const fetchSearchInventoryCategory = async (query) => {
    try {
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getSearchInventoryItem`,
        { userid: userID, searchWord: query },
        config
      );

      if (res?.data?.responseCode === '00') {
        const list = res?.data?.response || [];

        // Normalize to InventoryCard fields safely
        const norm = list.map((r, idx) => ({
          id: r.id || r.itemId || r.productId || `sr-${idx}`,
          productImage: r.productImage || r.image || r.subCategoryImage || '',
          productName: r.productName || r.itemName || r.name || r.category || 'Item',
          category: r.category || r.categoryName || '',
          subCategory: r.subCategory || r.subCategoryName || '',
          weight: r.weight || r.size || '',
          price: r.price || r.mrp || r.rate || '0.00',
          discount: r.discount || r.discountPrice || '0.00',
          // keep raw record if needed later
          __raw: r,
        }));
        return norm;
      }
      return [];
    } catch (err) {
      console.error('getSearchInventoryCategory error:', err);
      return [];
    }
  };
  // Debounced server search
  useEffect(() => {
    let t;
    const q = (searchWord || '').trim();

    // If cleared or too short, reset results
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return () => clearTimeout(t);
    }

    setSearchLoading(true);
    t = setTimeout(async () => {
      const results = await fetchSearchInventoryCategory(q);
      setSearchResults(results);
      setSearchLoading(false);
    }, 500); // debounce 400ms

    return () => clearTimeout(t);
  }, [searchWord]);
  useEffect(() => {
    console.log('clicked work')
  }, [showAddCategory, showAddSubCategory])




  return (
    <div className="gi-wrapper">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      {/* Selection Bar */}
      {selectedItems.length > 0 && (
        <div className="gi-float-panel" draggable>
          <div className="gi-float-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PackageCheck size={18} />
              <h4>{selectedItems.length} Selected</h4>
            </div>
            <button className="gi-clear-btn" onClick={() => setSelectedItems([])}>
              <XCircle size={18} />
            </button>
          </div>

          <ul className="gi-float-list">
            {inventoryItems
              .filter(i => selectedItems.includes(i.id))
              .map(i => (
                <li key={i.id}>
                  <img src={i.productImage} alt={i.productName} />
                  <span>{i.productName}</span>
                  <button onClick={() => toggleItemSelection(i.id)}><X size={14} /></button>
                </li>
              ))}
          </ul>

          <button className="gi-float-confirm" onClick={addSelectedToInventory}>
            Add to Main Inventory
          </button>
        </div>
      )}



      {/* Heading + Search */}
      <div className="gi-header">
        <h2 className="CreateCampaign">Global Inventory</h2>

        <div className="gi-search-wrap">
          <input
            type="text"
            placeholder="Search by name, category…"
            className="gi-search"
            value={searchWord}
            onChange={(e) => setSearchWord(e.target.value)}
          />
          {searchWord && (
            <button
              type="button"
              className="gi-search-clear"
              onClick={() => { setSearchWord(''); setSearchResults([]); }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Search Results (appear above categories) */}
      {(searchWord.trim().length >= 2) && (
        <div className="gi-search-results">
          <div className="gi-topprod-header">
            <div className="gi-topprod-title">
              <Boxes size={18} className="gi-icon" />
              <h3>Search Results</h3>
            </div>
            {/* Reuse your floating confirm for bulk add; no extra button needed here */}
          </div>

          {searchLoading ? (
            <div className="gi-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="gi-card shimmer" />
              ))}
            </div>
          ) : searchResults.length === 0 ? (
            <div className="gi-empty">No results for “{searchWord}”.</div>
          ) : (
            <div className="gi-grid">
              {searchResults.map((item) => (
                <InventoryCard
                  key={item.id}
                  item={item}
                  selected={selectedItems.includes(item.id)}
                  onSelect={toggleItemSelection}
                  onBarcode={(it) => setBarcodeItem(it)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Categories */}
      {/* Categories (scoped, new styles) */}

      <div className='border rounded bg-light p-3'>
        <div className="gi-cat-shelf">
          <div className="gi-cat-header">
            <div className="gi-cat-title">
              <FolderKanban size={18} className="gi-cat-icon" />
              <h3>Categories</h3>
            </div>

            <button
              type="button"
              className="gi-add-category-btn"
              // onClick={handleAddCategory}
              onClick={() => setShowAddCategory(true)}
            >
              + Add Category
            </button>
          </div>

          <div className="gi-cat-grid" /* no ref, no hover handlers */>
            {loadingCategories
              ? Array.from({ length: 10 }).map((_, i) => (
                <span key={i} className="gi-cat-chip gi-cat-chip--skeleton" />
              ))
              : masterCategories.map((cat) => (
                <button
                  type="button"
                  key={cat.id}
                  className={`gi-cat-chip ${selectedCategoryId === cat.id ? "gi-cat-chip--active" : ""
                    }`}
                  onClick={() => handleCategorySelect(cat.id, cat.subCategories)}
                  aria-pressed={selectedCategoryId === cat.id}
                  title={cat.category}
                >
                  <span className="gi-cat-chip__dot" aria-hidden="true" />
                  <span className="gi-cat-chip__label">{cat.category}</span>
                </button>
              ))}
          </div>
        </div>
      </div>




      {/* Subcategories */}
      <div className="gi-subcategory-wrapper-box sticky-subcategories">
        <div className="gi-subcat-header">
          <div className="gi-subcat-title">
            <LayoutGrid size={18} className="gi-icon" />
            <h3>Subcategories</h3>
          </div>

          <button
            type="button"
            className="gi-add-subcategory-btn"
            // onClick={handleAddSubcategory} 
            onClick={() => {
              if (!selectedCategoryName) {
                // Optional UX: stop user if no category selected
                toast.info('Select a category first');
                return;
              }
              setShowAddSubCategory(true);
            }}
          >
            + Add Subcategory
          </button>
        </div>
        <div className="gi-subcategory-wrapper">
          {loadingCategories
            ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="gi-subcard shimmer" />
            ))
            : subCategories.map((sub) => (
              <div
                className={`gi-subcard ${selectedSubCategoryId === sub.id ? 'gi-sub-active' : ''}`}
                key={sub.id}
                onClick={() => {
                  setSelectedSubCategoryId(sub.id);
                  fetchProductsByCategory(sub.id);
                }}
              >
                <img src={sub.subCategoryImage} alt={sub.subCategory} className="gi-subcard-img" />
                <div className="gi-subcard-name">{sub.subCategory}</div>
              </div>
            ))}
        </div>
      </div>

      {/* Products */}
      <div className="gi-topprod-header" ref={productRef}>
        <div className="gi-topprod-title">
          <Boxes size={18} className="gi-icon" />
          <h3>Top Products</h3>
        </div>

        <button
          type="button"
          className="gi-topprod-add-btn"
          onClick={() => setShowAddItem(true)}
        >
          + Add Item
        </button>
      </div>


      {loadingItems || inventoryItems.length === 0 ? (
        <div className="gi-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="gi-card shimmer" />
          ))}
        </div>
      ) : (
        <div className="gi-grid">
          {filteredItems.map((item) => (
            <InventoryCard
              key={item.id}
              item={item}
              selected={selectedItems.includes(item.id)}
              onSelect={toggleItemSelection}
              onBarcode={(item) => setBarcodeItem(item)}
            />
          ))}
          {barcodeItem && (
            <AddBarcodeModal
              item={barcodeItem}
              userID={userID}
              baseUrl={baseUrl}
              config={config}
              onClose={() => setBarcodeItem(null)}
            />
          )}

        </div>
      )}
      <AddItemPrefilledModal
        show={showAddItem}
        onClose={() => setShowAddItem(false)}
        userID={userID}
        baseUrl={baseUrl}
        BASIC_AUTH={BASIC_AUTH}
        prefilledCategoryName={selectedCategoryName}
        prefilledSubCategoryName={effectiveSubCategoryName}  // ⬅️ use fallback
      />
      <AddInventoryCategoryModal
        show={showAddCategory}
        onClose={() => setShowAddCategory(false)}
        userID={userID}
        baseUrl={baseUrl}
        onSuccess={() => {
          // reload categories after adding
          fetchMasterCategories();
        }}
      />
      <AddInventorySubCategoryModal
        show={showAddSubCategory}
        onClose={() => setShowAddSubCategory(false)}
        userID={userID}
        baseUrl={baseUrl}
        selectedCategoryName={selectedCategoryName}
        onSuccess={() => {
          // reload categories (and implicitly subcategories for the selected master category)
          fetchMasterCategories();
        }}
      />
    </div>
  );
};

export default GlobalInventory;
